import type {
  Assignment,
  AssignmentDeliveryRule,
  AssignmentDeliveryRuleReviewer,
  AssignmentDeliveryRuleTarget,
  AssignmentSubmission
} from '../assignment/model';
import type { Notification, NotificationRead } from '../notification/model';
import type {
  AttendanceCategory,
  AttendanceEvent,
  AttendanceEventMemberGroup,
  AttendanceParticipantSnapshot,
  AttendanceRecord,
  Schedule
} from '../schedule/model';
import type { ResourceFile, ResourceFolder, Song } from '../resources/model';

export type MemberRole = 'admin' | 'member';
export type PermissionKey = 'member_approval' | 'schedule_manage' | 'assignment_publish_manage';
export type SeasonMembershipStatus = 'active';
export type InvitationCodeStatus = 'active' | 'deleted';
export type JoinRequestStatus = 'pending' | 'approved' | 'rejected';

export const CURRENT_MEMBER_MANAGEMENT_DATA_VERSION = 8;
export const MEMBER_MANAGEMENT_DATA_RESET_REQUIRED_MESSAGE = '本地成员管理 MVP 数据已过期，需要重置后继续使用。';

export interface DefaultMemberGroupDefinition {
  readonly name: string;
  readonly isVocalPart: boolean;
}

export interface UserAccount {
  readonly userId: string;
  readonly email: string;
  readonly createdAt: string;
}

export interface Choir {
  readonly choirId: string;
  readonly name: string;
  readonly createdByUserId: string;
  readonly createdAt: string;
}

export interface Season {
  readonly seasonId: string;
  readonly choirId: string;
  readonly name: string;
  readonly isCurrent: boolean;
  readonly createdAt: string;
}

export interface Membership {
  readonly membershipId: string;
  readonly userId: string;
  readonly choirId: string;
  readonly role: MemberRole;
  readonly createdAt: string;
}

export interface SeasonMembership {
  readonly seasonMembershipId: string;
  readonly membershipId: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly nickname: string;
  readonly status: SeasonMembershipStatus;
  readonly createdAt: string;
}

export interface MemberGroup {
  readonly memberGroupId: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly name: string;
  readonly isVocalPart: boolean;
  readonly isDefault: boolean;
  readonly displayOrder: number;
  readonly createdBySeasonMembershipId: string | null;
  readonly createdAt: string;
}

export interface SeasonMembershipMemberGroup {
  readonly seasonMembershipId: string;
  readonly memberGroupId: string;
}

export interface MemberGroupManagerGrant {
  readonly memberGroupId: string;
  readonly seasonMembershipId: string;
  readonly grantedAt: string;
}

export interface MemberGroupTag {
  readonly memberGroupTagId: string;
  readonly memberGroupId: string;
  readonly name: string;
  readonly displayOrder: number;
  readonly createdAt: string;
}

export interface SeasonMembershipMemberGroupTag {
  readonly memberGroupTagId: string;
  readonly seasonMembershipId: string;
}

export interface PermissionGroup {
  readonly permissionGroupId: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly name: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface PermissionGroupPermission {
  readonly permissionGroupId: string;
  readonly permissionKey: PermissionKey;
}

export interface PermissionGroupMember {
  readonly permissionGroupId: string;
  readonly seasonMembershipId: string;
  readonly grantedAt: string;
}

export interface InvitationCode {
  readonly invitationCodeId: string;
  readonly code: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly status: InvitationCodeStatus;
  readonly createdByMembershipId: string;
  readonly createdAt: string;
  readonly deletedAt: string | null;
  readonly expiresAt: string;
}

export interface JoinRequest {
  readonly joinRequestId: string;
  readonly userId: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly invitationCodeId: string;
  readonly nickname: string;
  readonly status: JoinRequestStatus;
  readonly submittedAt: string;
  readonly reviewedByMembershipId: string | null;
  readonly reviewedAt: string | null;
  readonly rejectionReason: string | null;
}

export interface FileUpload {
  readonly fileUploadId: string;
  readonly purpose: 'assignment_submission' | 'resource_file';
  readonly status: 'pending' | 'completed' | 'abandoned';
  readonly choirId: string;
  readonly seasonId: string;
  readonly assignmentId: string | null;
  readonly createdByUserId: string;
  readonly createdBySeasonMembershipId: string;
  readonly objectKey: string;
  readonly fileName: string;
  readonly mimeType: string;
  readonly extension: string;
  readonly sizeBytes: number;
  readonly etag: string | null;
  readonly expiresAt: string;
  readonly createdAt: string;
  readonly completedAt: string | null;
  readonly attachedAt: string | null;
}

export interface ObjectCleanupFailure {
  readonly objectCleanupFailureId: string;
  readonly objectKey: string;
  readonly purpose: 'assignment_submission_overwrite' | 'song_current_score_replace';
  readonly relatedEntityType: 'assignment_submission' | 'song';
  readonly relatedEntityId: string;
  readonly failureMessage: string;
  readonly status: 'pending' | 'resolved';
  readonly createdAt: string;
  readonly resolvedAt: string | null;
}

export interface MemberManagementData {
  readonly version: number;
  readonly users: readonly UserAccount[];
  readonly choirs: readonly Choir[];
  readonly seasons: readonly Season[];
  readonly memberships: readonly Membership[];
  readonly seasonMemberships: readonly SeasonMembership[];
  readonly memberGroups: readonly MemberGroup[];
  readonly seasonMembershipMemberGroups: readonly SeasonMembershipMemberGroup[];
  readonly memberGroupManagerGrants: readonly MemberGroupManagerGrant[];
  readonly memberGroupTags: readonly MemberGroupTag[];
  readonly seasonMembershipMemberGroupTags: readonly SeasonMembershipMemberGroupTag[];
  readonly permissionGroups: readonly PermissionGroup[];
  readonly permissionGroupPermissions: readonly PermissionGroupPermission[];
  readonly permissionGroupMembers: readonly PermissionGroupMember[];
  readonly invitationCodes: readonly InvitationCode[];
  readonly joinRequests: readonly JoinRequest[];
  readonly schedules: readonly Schedule[];
  readonly attendanceCategories: readonly AttendanceCategory[];
  readonly attendanceEvents: readonly AttendanceEvent[];
  readonly attendanceEventMemberGroups: readonly AttendanceEventMemberGroup[];
  readonly attendanceParticipantSnapshots: readonly AttendanceParticipantSnapshot[];
  readonly attendanceRecords: readonly AttendanceRecord[];
  readonly assignments: readonly Assignment[];
  readonly assignmentDeliveryRules: readonly AssignmentDeliveryRule[];
  readonly assignmentDeliveryRuleTargets: readonly AssignmentDeliveryRuleTarget[];
  readonly assignmentDeliveryRuleReviewers: readonly AssignmentDeliveryRuleReviewer[];
  readonly fileUploads: readonly FileUpload[];
  readonly assignmentSubmissions: readonly AssignmentSubmission[];
  readonly objectCleanupFailures: readonly ObjectCleanupFailure[];
  readonly songs: readonly Song[];
  readonly resourceFolders: readonly ResourceFolder[];
  readonly resourceFiles: readonly ResourceFile[];
  readonly notifications: readonly Notification[];
  readonly notificationReads: readonly NotificationRead[];
}

export const DEFAULT_MEMBER_GROUPS: readonly DefaultMemberGroupDefinition[] = [
  { name: 'Soprano', isVocalPart: true },
  { name: 'Alto', isVocalPart: true },
  { name: 'Tenor', isVocalPart: true },
  { name: 'Bass', isVocalPart: true },
  { name: '幕后', isVocalPart: false },
  { name: '钢伴', isVocalPart: false },
  { name: '指挥', isVocalPart: false }
] as const;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function createEmptyMemberManagementData(): MemberManagementData {
  return {
    version: CURRENT_MEMBER_MANAGEMENT_DATA_VERSION,
    users: [],
    choirs: [],
    seasons: [],
    memberships: [],
    seasonMemberships: [],
    memberGroups: [],
    seasonMembershipMemberGroups: [],
    memberGroupManagerGrants: [],
    memberGroupTags: [],
    seasonMembershipMemberGroupTags: [],
    permissionGroups: [],
    permissionGroupPermissions: [],
    permissionGroupMembers: [],
    invitationCodes: [],
    joinRequests: [],
    schedules: [],
    attendanceCategories: [],
    attendanceEvents: [],
    attendanceEventMemberGroups: [],
    attendanceParticipantSnapshots: [],
    attendanceRecords: [],
    assignments: [],
    assignmentDeliveryRules: [],
    assignmentDeliveryRuleTargets: [],
    assignmentDeliveryRuleReviewers: [],
    fileUploads: [],
    assignmentSubmissions: [],
    objectCleanupFailures: [],
    songs: [],
    resourceFolders: [],
    resourceFiles: [],
    notifications: [],
    notificationReads: []
  };
}

export function assertCurrentMemberManagementDataVersion(data: MemberManagementData): void {
  if (data.version !== CURRENT_MEMBER_MANAGEMENT_DATA_VERSION) {
    throw new Error(MEMBER_MANAGEMENT_DATA_RESET_REQUIRED_MESSAGE);
  }
}

export function normalizeEmail(rawEmail: string): string {
  const email = rawEmail.trim().toLowerCase();

  if (email === '') {
    throw new Error('Email is required');
  }

  if (!EMAIL_PATTERN.test(email)) {
    throw new Error('Email must be valid');
  }

  return email;
}

export function normalizeRequiredText(rawText: string, label: string): string {
  const text = rawText.trim();

  if (text === '') {
    throw new Error(`${label} is required`);
  }

  return text;
}
